import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Users,
  Calendar,
  Loader2,
  Minus,
} from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

interface ForecastDataPoint {
  date: string;
  actual?: number;
  predicted: number;
  lower_bound: number;
  upper_bound: number;
}

interface ChurnRiskCustomer {
  customer_id: number;
  customer_name: string;
  email: string;
  churn_probability: number;
  risk_level: 'High' | 'Medium' | 'Low';
  contributing_factors: string[];
  last_interaction_days: number;
}

interface StaffingForecast {
  date: string;
  forecasted_volume: number;
  agents_required: number;
  current_agents: number;
  gap: number;
}

interface PredictiveData {
  volume_forecast: ForecastDataPoint[];
  churn_risks: ChurnRiskCustomer[];
  staffing_forecast: StaffingForecast[];
  insights: {
    peak_days: string[];
    avg_daily_volume: number;
    trend_direction: 'increasing' | 'decreasing' | 'stable';
    high_risk_customers: number;
  };
}

interface PredictiveAnalyticsProps {
  startDate: string;
  endDate: string;
  forecastDays?: number;
}

const getRiskVariant = (level: string): 'destructive' | 'secondary' | 'outline' => {
  if (level === 'High') return 'destructive';
  if (level === 'Medium') return 'secondary';
  return 'outline';
};

const PredictiveAnalytics: React.FC<PredictiveAnalyticsProps> = ({
  startDate,
  endDate,
  forecastDays = 30,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PredictiveData | null>(null);

  useEffect(() => {
    fetchPredictiveData();
  }, [startDate, endDate, forecastDays]);

  const fetchPredictiveData = async () => {
    setLoading(true);
    setError(null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // const response = await advancedReportsAPI.getPredictiveAnalytics({
      //   start_date: startDate,
      //   end_date: endDate,
      //   forecast_days: forecastDays
      // });

      // Mock data matching ADVANCED_ANALYTICS_SCENARIOS.md structure
      const mockForecast: ForecastDataPoint[] = [];
      const today = new Date();

      // Generate 30 days of historical data (actual)
      for (let i = -30; i < 0; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const baseValue = 150 + Math.sin(i / 7) * 30;
        mockForecast.push({
          date: date.toISOString().split('T')[0],
          actual: Math.floor(baseValue + Math.random() * 20),
          predicted: 0,
          lower_bound: 0,
          upper_bound: 0,
        });
      }

      // Generate 30 days of forecast data (predicted)
      for (let i = 0; i < forecastDays; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() + i);
        const baseValue = 160 + Math.sin(i / 7) * 30 + i * 0.5; // Slight upward trend
        mockForecast.push({
          date: date.toISOString().split('T')[0],
          predicted: Math.floor(baseValue),
          lower_bound: Math.floor(baseValue - 25),
          upper_bound: Math.floor(baseValue + 25),
        });
      }

      const mockData: PredictiveData = {
        volume_forecast: mockForecast,
        churn_risks: [
          {
            customer_id: 1,
            customer_name: 'Acme Corp',
            email: 'contact@acme.com',
            churn_probability: 0.85,
            risk_level: 'High',
            contributing_factors: [
              'Multiple unresolved tickets',
              'Declining CSAT scores',
              'Long resolution times',
            ],
            last_interaction_days: 45,
          },
          {
            customer_id: 2,
            customer_name: 'TechStart Inc',
            email: 'support@techstart.com',
            churn_probability: 0.72,
            risk_level: 'High',
            contributing_factors: ['Frequent escalations', 'Negative sentiment'],
            last_interaction_days: 30,
          },
          {
            customer_id: 3,
            customer_name: 'Global Systems',
            email: 'help@globalsys.com',
            churn_probability: 0.58,
            risk_level: 'Medium',
            contributing_factors: ['Slow response times'],
            last_interaction_days: 20,
          },
        ],
        staffing_forecast: Array.from({ length: forecastDays }, (_, i) => {
          const date = new Date(today);
          date.setDate(date.getDate() + i);
          const volume = 160 + Math.sin(i / 7) * 30 + i * 0.5;
          const required = Math.ceil(volume / 20 / 0.8); // 20 conv/agent, 80% utilization
          const current = 8;
          return {
            date: date.toISOString().split('T')[0],
            forecasted_volume: Math.floor(volume),
            agents_required: required,
            current_agents: current,
            gap: required - current,
          };
        }),
        insights: {
          peak_days: ['Monday', 'Wednesday'],
          avg_daily_volume: 165,
          trend_direction: 'increasing',
          high_risk_customers: 2,
        },
      };

      setData(mockData);
    } catch (err: any) {
      console.error('Failed to fetch predictive analytics:', err);
      setError(err.message || 'Failed to load predictive analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert className="m-2">
        <AlertDescription>No predictive analytics data available.</AlertDescription>
      </Alert>
    );
  }

  // Forecast chart options — untouched
  const forecastChartOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: {
        show: true,
        tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      zoom: { enabled: true },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [2, 2, 0, 0] },
    fill: {
      type: ['solid', 'solid', 'gradient', 'gradient'],
      gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.1, stops: [0, 90, 100] },
    },
    xaxis: {
      categories: data.volume_forecast.map((d) => d.date),
      labels: { rotate: -45, rotateAlways: true },
    },
    yaxis: { title: { text: 'Conversations' } },
    colors: ['#4caf50', '#1f93ff', '#e3f2fd', '#e3f2fd'],
    legend: { position: 'top' },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => (val ? `${Math.round(val)} conversations` : '') },
    },
    annotations: {
      xaxis: [
        {
          x: new Date().toISOString().split('T')[0],
          borderColor: '#f44336',
          label: {
            style: { color: '#fff', background: '#f44336' },
            text: 'Today',
          },
        },
      ],
    },
  };

  const forecastSeries = [
    { name: 'Actual', type: 'line', data: data.volume_forecast.map((d) => d.actual || null) },
    { name: 'Predicted', type: 'line', data: data.volume_forecast.map((d) => d.predicted || null) },
    { name: 'Upper Bound', type: 'area', data: data.volume_forecast.map((d) => d.upper_bound || null) },
    { name: 'Lower Bound', type: 'area', data: data.volume_forecast.map((d) => d.lower_bound || null) },
  ];

  const TrendIcon =
    data.insights.trend_direction === 'increasing' ? TrendingUp :
    data.insights.trend_direction === 'decreasing' ? TrendingDown :
    Minus;

  return (
    <div className="space-y-6">
      {/* Key Insights */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm flex flex-col">
          <CardHeader className="flex-1">
            <CardDescription>Trend Direction</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold capitalize truncate">
              {data.insights.trend_direction}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Next {forecastDays} days</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm flex flex-col">
          <CardHeader className="flex-1">
            <CardDescription>Avg Daily Volume</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.insights.avg_daily_volume}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Forecasted conversations/day</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm flex flex-col">
          <CardHeader className="flex-1">
            <CardDescription>High-Risk Customers</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.insights.high_risk_customers}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Churn probability &gt; 70%</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm flex flex-col">
          <CardHeader className="flex-1">
            <CardDescription>Peak Days</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="pt-1">
              <div className="flex flex-wrap gap-1">
                {data.insights.peak_days.map((day) => (
                  <Badge key={day} variant="secondary" className="text-xs">
                    {day}
                  </Badge>
                ))}
              </div>
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Highest conversation volume</span>
          </CardFooter>
        </Card>

      </div>

      {/* Prediction Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="volume">
            <TabsList className="mb-4">
              <TabsTrigger value="volume" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Volume Forecast
              </TabsTrigger>
              <TabsTrigger value="churn" className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Churn Risk
              </TabsTrigger>
              <TabsTrigger value="staffing" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Staffing Forecast
              </TabsTrigger>
            </TabsList>

            {/* Volume Forecast */}
            <TabsContent value="volume" className="space-y-4">
              <div>
                <h3 className="text-sm font-medium">
                  Conversation Volume Forecast ({forecastDays} Days)
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Time series prediction with confidence intervals using historical patterns and
                  seasonality
                </p>
              </div>
              <Chart
                options={forecastChartOptions}
                series={forecastSeries}
                type="line"
                height={400}
              />
              <Alert>
                <AlertDescription>
                  <p className="font-medium text-sm mb-1">Forecast Insights</p>
                  <ul className="space-y-1 text-xs text-muted-foreground list-disc pl-4">
                    <li>
                      Expected {data.insights.trend_direction === 'increasing' ? 'increase' : 'decrease'} in
                      conversation volume over the next {forecastDays} days
                    </li>
                    <li>Peak volume expected on {data.insights.peak_days.join(' and ')}</li>
                    <li>
                      Average daily volume forecast: {data.insights.avg_daily_volume} conversations
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </TabsContent>

            {/* Churn Risk */}
            <TabsContent value="churn" className="space-y-4">
              <div>
                <h3 className="text-sm font-medium">Customer Churn Risk Prediction</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  AI-powered prediction based on support interactions, sentiment, and engagement
                  patterns
                </p>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-center">Churn Risk</TableHead>
                    <TableHead className="text-center">Risk Level</TableHead>
                    <TableHead>Contributing Factors</TableHead>
                    <TableHead className="text-right">Last Interaction</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.churn_risks.map((customer) => (
                    <TableRow key={customer.customer_id}>
                      <TableCell className="text-sm font-medium">
                        {customer.customer_name}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {customer.email}
                      </TableCell>
                      <TableCell className="text-center">
                        <div>
                          <p className="text-sm font-bold">
                            {(customer.churn_probability * 100).toFixed(0)}%
                          </p>
                          <Progress
                            value={customer.churn_probability * 100}
                            className="h-1.5 mt-1"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={getRiskVariant(customer.risk_level)}>
                          {customer.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {customer.contributing_factors.map((factor, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {factor}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">
                        {customer.last_interaction_days} days ago
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Alert>
                <AlertDescription>
                  <p className="font-medium text-sm mb-1">Recommended Actions</p>
                  <ul className="space-y-1 text-xs text-muted-foreground list-disc pl-4">
                    <li>
                      Immediately reach out to{' '}
                      {data.churn_risks.filter((c) => c.risk_level === 'High').length} high-risk
                      customers
                    </li>
                    <li>Review and resolve outstanding issues for at-risk accounts</li>
                    <li>Schedule check-in calls with customers showing declining engagement</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </TabsContent>

            {/* Staffing Forecast */}
            <TabsContent value="staffing" className="space-y-4">
              <div>
                <h3 className="text-sm font-medium">Staffing Requirements Forecast</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Agent capacity planning based on predicted conversation volume (20 conv/agent/day,
                  80% utilization)
                </p>
              </div>
              <div className="max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Forecasted Volume</TableHead>
                      <TableHead className="text-right">Agents Required</TableHead>
                      <TableHead className="text-right">Current Agents</TableHead>
                      <TableHead className="text-center">Gap</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.staffing_forecast.slice(0, 14).map((day) => (
                      <TableRow key={day.date}>
                        <TableCell className="text-sm">
                          <span className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-muted-foreground" />
                            {new Date(day.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm">
                          {day.forecasted_volume}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline">{day.agents_required}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">{day.current_agents}</TableCell>
                        <TableCell className="text-center">
                          {day.gap > 0 ? (
                            <Badge variant="destructive">-{day.gap}</Badge>
                          ) : day.gap < -1 ? (
                            <Badge variant="secondary">+{Math.abs(day.gap)}</Badge>
                          ) : (
                            <Badge variant="outline">OK</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Alert>
                <AlertDescription>
                  <p className="font-medium text-sm mb-1">Capacity Planning Insights</p>
                  <ul className="space-y-1 text-xs text-muted-foreground list-disc pl-4">
                    <li>
                      Staffing shortage expected on{' '}
                      {data.staffing_forecast.filter((d) => d.gap > 0).length} days in the next 2
                      weeks
                    </li>
                    <li>
                      Consider hiring{' '}
                      {Math.max(...data.staffing_forecast.map((d) => d.gap))} additional agents to
                      meet peak demand
                    </li>
                    <li>Schedule part-time or flex agents for high-volume days</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Predictive Model Info */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">About Predictive Models</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium mb-1">Volume Forecasting</p>
              <p className="text-xs text-muted-foreground">
                Uses time series analysis (Prophet algorithm) with weekly and yearly seasonality
                patterns. Trained on 90+ days of historical data.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Churn Prediction</p>
              <p className="text-xs text-muted-foreground">
                Random Forest classifier analyzing 7+ features including CSAT, resolution times,
                sentiment, and engagement patterns.
              </p>
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Staffing Optimization</p>
              <p className="text-xs text-muted-foreground">
                Capacity model based on forecasted volume, agent productivity benchmarks, and target
                utilization rates.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PredictiveAnalytics;
